'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { ChevronLeft, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import type { OnboardingData } from './OnboardingFlow';

interface Props {
  data: OnboardingData;
  onChange: (partial: Partial<OnboardingData>) => void;
  onComplete: () => void;
  onBack: () => void;
}

type FormValues = {
  name: string;
  email: string;
  password: string;
};

export default function StepAuth({ data, onComplete, onBack }: Props) {
  const [isLogin, setIsLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true);
    try {
      if (isLogin) {
        await signIn(values.email, values.password);
        toast.success('Welcome back 💜');
      } else {
        await signUp(values.email, values.password, {
          fullName: values.name || '',
        });
        toast.success(`Welcome, ${values.name || 'friend'} 💜`);
      }
      onComplete();
    } catch (err: any) {
      toast.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col px-6 py-8 max-w-sm mx-auto w-full slide-up">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-muted-foreground text-sm mb-8 hover:text-foreground transition-colors"
      >
        <ChevronLeft size={18} />
        Back
      </button>

      <div className="flex-1 flex flex-col gap-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            {isLogin ? 'Welcome back 💜' : 'Create your space'}
          </h2>
          <p className="text-sm text-muted-foreground mt-2">
            {isLogin
              ? 'Your conversations and reflections are waiting.' :'Your data is private and only yours.'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
          {!isLogin && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold text-foreground">
                What should Clingy call you?
              </label>
              <input
                type="text"
                placeholder="Your name or nickname"
                className={`w-full px-4 py-3.5 rounded-2xl border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary ${
                  errors.name ? 'border-red-400' : 'border-border'
                }`}
                {...register('name', {
                  required: !isLogin ? 'Tell Clingy what to call you' : false,
                })}
              />
              {errors.name && (
                <p className="text-xs text-red-500">{errors.name.message}</p>
              )}
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-foreground">Email</label>
            <input
              type="email"
              placeholder="you@example.com"
              className={`w-full px-4 py-3.5 rounded-2xl border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary ${
                errors.email ? 'border-red-400' : 'border-border'
              }`}
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' },
              })}
            />
            {errors.email && (
              <p className="text-xs text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold text-foreground">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={isLogin ? '••••••••' : 'At least 8 characters'}
                className={`w-full px-4 py-3.5 pr-12 rounded-2xl border bg-input text-foreground placeholder:text-muted-foreground text-sm outline-none transition-all duration-200 focus:shadow-input focus:border-primary ${
                  errors.password ? 'border-red-400' : 'border-border'
                }`}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'At least 8 characters' },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-500">{errors.password.message}</p>
            )}
            {/* Forgot password — only shown on login */}
            {isLogin && (
              <div className="flex justify-end mt-0.5">
                <a
                  href="/forgot-password"
                  className="text-xs text-primary font-semibold hover:underline"
                >
                  Forgot password?
                </a>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-2xl lavender-gradient text-white font-semibold text-base shadow-card transition-all duration-200 active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2 mt-2"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                <span>{isLogin ? 'Signing in…' : 'Creating your space…'}</span>
              </>
            ) : (
              <span>{isLogin ? 'Sign In' : 'Create My Space 💜'}</span>
            )}
          </button>
        </form>

        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        {/* Toggle login/signup */}
        <p className="text-center text-sm text-muted-foreground">
          {isLogin ? "Don't have an account? " : 'Already have an account? '}
          <button
            type="button"
            onClick={() => setIsLogin((v) => !v)}
            className="text-primary font-semibold hover:underline"
          >
            {isLogin ? 'Sign up' : 'Sign in'}
          </button>
        </p>

        <button
          type="button"
          onClick={() => router.push('/chat-screen?guest=true')}
          className="w-full py-3 rounded-2xl border border-border bg-transparent text-muted-foreground font-semibold text-sm transition-all duration-200 hover:border-primary/40 hover:text-foreground"
        >
          Continue without an account
        </button>

        {!isLogin && (
          <p className="text-center text-xs text-muted-foreground leading-relaxed">
            By creating an account you agree to our{' '}
            <Link href="/terms" className="text-primary hover:underline">Terms</Link>
            {' '}and{' '}
            <Link href="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.
          </p>
        )}
      </div>
    </div>
  );
}